import { RegisterUserType, LoginUserType } from './types';
import { AppThunk } from '../../types';
import moment from 'moment';
import history from '../../../utils/browserHistory';
import { getGigyaErrorMessage, decodeToken } from './utils';
import { authService } from './authService';
import authSlice from './authSlice';
import featureFlagsSlice from '../featureFlags/featureFlagsSlice';
import intlSlice from '../intl/intlSlice';
import { getMomentLocaleCode } from '../../../utils/momentUtils';
import { SessionStorageUtil } from '../../../utils/SessionStorageUtil';

//---------------------------------------------------------------------------------------------
// --------------------------------- Login actions  -------------------------------------------
//---------------------------------------------------------------------------------------------

const login = (
  { email, password }: LoginUserType,
  redirectUserHome = true,
  redirectTarget?: string | false | null,
): AppThunk => {
  return dispatch => {
    dispatch(authSlice.actions.requestAuthStart());

    const callbackGigya = (response: any) => {
      const { UID, UIDSignature, signatureTimestamp } = response;
      const error = getGigyaErrorMessage(response);

      if (error) {
        return dispatch(authSlice.actions.requestAuthFailure(error));
      }

      authService
        .getToken({
          UID,
          UIDSignature,
          signatureTimestamp,
        })
        .then(
          user => {
            const { token }: any = user;

            const { country, language } = decodeToken(token);
            const locale = `${language}_${country}`;
            const homeUrl = `/${country}/${locale}`;
            const momentLocaleCode = getMomentLocaleCode(locale);

            dispatch(authSlice.actions.requestSuccessLogin(token));
            dispatch(featureFlagsSlice.actions.setTokenFeatures(token));

            import(`moment/locale/${momentLocaleCode}`)
              .then(t => {
                moment.locale(momentLocaleCode);
                return t.default;
              })
              .catch(() => ({}));

            if (redirectUserHome) {
              if (redirectTarget) {
                history.push(redirectTarget);
              } else {
                dispatch(intlSlice.actions.changeLocale(locale));
                history.push(homeUrl);
              }
            }
          },
          error => {
            if (error.errorCode === 'AUTH__PROFILE_INCOMPLETE') {
              const pathname = window.location.pathname.split('/');
              const countryCode = pathname[1];
              const locale = pathname[2];
              const prefix = `/${countryCode}/${locale}`;
              const completeRegistrationPageUrl = `${prefix}/complete-registration`;

              SessionStorageUtil.set(
                SessionStorageUtil.KEY_NAMES.EMAIL_ID,
                email,
              );
              SessionStorageUtil.set(
                SessionStorageUtil.KEY_NAMES.PASSWORD,
                password,
              );

              dispatch(authSlice.actions.resetAuthState());
              history.push({
                pathname: completeRegistrationPageUrl,
                state: {
                  password,
                  email,
                },
              });
            } else {
              dispatch(authSlice.actions.requestAuthFailure(error));
            }
          },
        );
    };

    authService.login({ email, password }, callbackGigya);
  };
};

//---------------------------------------------------------------------------------------------
// --------------------------------- Register actions  ----------------------------------------
//---------------------------------------------------------------------------------------------

export const register = (
  user: RegisterUserType,
  recaptchaToken: string,
): AppThunk => dispatch => {
  authService.register(user, recaptchaToken).then(
    () => {
      dispatch(login({ email: user.email, password: user.password }, false));
      dispatch(authSlice.actions.requestAuthSuccess());
      dispatch(authSlice.actions.setIsRegistering(true));
    },
    error => {
      dispatch(authSlice.actions.requestAuthFailure(error));
    },
  );
};

//---------------------------------------------------------------------------------------------
// --------------------------------- Complete Registration actions  ----------------------------------------
//---------------------------------------------------------------------------------------------

export const completeRegistration = (
  user: RegisterUserType,
  recaptchaToken: string,
): AppThunk => dispatch => {
  authService.completeRegistration(user, recaptchaToken).then(
    () => {
      dispatch(login({ email: user.email, password: user.password }, false));
      dispatch(authSlice.actions.requestAuthSuccess());
      dispatch(authSlice.actions.setIsRegistering(true));
    },
    error => {
      dispatch(authSlice.actions.requestAuthFailure(error));
    },
  );
};

//---------------------------------------------------------------------------------------------
// --------------------------------- Logout actions  ----------------------------------------
//---------------------------------------------------------------------------------------------

export const logout = (): AppThunk => dispatch => {
  dispatch(authSlice.actions.logout());
  dispatch(featureFlagsSlice.actions.removeTokenFeatures());
};

//---------------------------------------------------------------------------------------------
// --------------------------------- Set New Password actions  ----------------------------------------
//---------------------------------------------------------------------------------------------

type SetNewPassword = {
  email: string;
  password: string;
  pwrt: string;
  isMobile: boolean;
};

const setNewPassword = ({
  email,
  password,
  pwrt,
  isMobile,
}: SetNewPassword): AppThunk => {
  return dispatch => {
    dispatch(authSlice.actions.requestAuthStart());

    const callbackGigya = (response: any) => {
      const error = getGigyaErrorMessage(response);

      if (error) {
        return dispatch(authSlice.actions.requestAuthFailure(error));
      } else {
        dispatch(authSlice.actions.requestAuthSuccess());
      }

      if (!isMobile) {
        dispatch(login({ email, password }));
      }
    };

    if (pwrt.length === 0) {
      return dispatch(
        authSlice.actions.requestAuthFailure({
          reason: 'TECH',
          message: 'component.ResetPassword.pwrtNotProvided',
        }),
      );
    }

    authService.resetPassword({ email, password, pwrt }, callbackGigya);
  };
};

export const userActions = {
  login,
  logout,
  register,
  setNewPassword,
  completeRegistration,
};
